import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, User as UserIcon, Phone, Sparkles, Loader2, ArrowRight, Inbox, Compass } from "lucide-react";
import { formatDate } from "../../lib/dateFormat";
import HelpPalette from "./HelpPalette";
import AviChatDrawer from "../ai/AviChatDrawer";
import { useSlideoverOpen } from "../../lib/slideoverStore";

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Pages catalog — fuels the "Jump to page" section of the ⌘K palette.
// Every Avida Health Hub page is listed here, INCLUDING hidden ones we
// trimmed from the sidebar. Users can still reach any of them by typing.
// Keep this in sync when new routes are added to App.js.
// ─────────────────────────────────────────────────────────────────────────────
const PAGES = [
  // Top-level
  { label: "Dashboard", path: "/healthcare", group: "Top", keywords: "home main" },
  { label: "My Tasks", path: "/healthcare/tasks", group: "Top", keywords: "queue todo inbox" },

  // Authorizations
  { label: "Authorizations", path: "/healthcare/authorization-management", group: "Authorizations", keywords: "auth pa prior authorization units hours mco" },
  { label: "Physician Orders", path: "/healthcare/auth/physician-orders", group: "Authorizations", keywords: "mpo md order" },

  // Authorizations
  // Authorization Hub + PA Tracker removed from spotlight per Dawn 2026-06-11.
  // Recertification + Supervisory Visits kept — RN ops still need them.
  { label: "Recertification", path: "/healthcare/pa-renewal", group: "Authorizations", keywords: "pa renewal" },
  { label: "Supervisory Visits", path: "/healthcare/supervisory-visits", group: "Authorizations", keywords: "rn sv 107" },

  // Clients
  { label: "Client Roster & Census", path: "/healthcare/patients", group: "Clients", keywords: "clients patients census roster hospitalized paused discharged" },
  { label: "Schedule", path: "/healthcare/schedule", group: "Clients", keywords: "calendar visits" },
  { label: "Incident Reporting", path: "/healthcare/incidents", group: "Clients", keywords: "incident report" },

  // Care Team
  { label: "RNs & PCWs", path: "/healthcare/caregivers", group: "Care Team", keywords: "staff caregivers nurses" },
  { label: "Add Staff", path: "/healthcare/caregivers/new", group: "Care Team", hidden: true },
  { label: "Client Assignments", path: "/healthcare/roster", group: "Care Team", keywords: "roster assignments" },
  { label: "Background Checks", path: "/healthcare/caregivers?tab=background", group: "Care Team", keywords: "bg check dhs" },
  { label: "Credential Dashboard", path: "/healthcare/staff-credentials", group: "Care Team", hidden: true, keywords: "credentials license" },
  { label: "Staff Eligibility", path: "/healthcare/staff-eligibility", group: "Care Team", hidden: true, keywords: "oig exclusion" },

  // Billing & Finance
  { label: "Billing Dashboard", path: "/healthcare/billing", group: "Billing & Finance" },
  { label: "Claims", path: "/healthcare/claims", group: "Billing & Finance", keywords: "837 claim" },
  { label: "Timesheets", path: "/healthcare/timesheets", group: "Billing & Finance" },
  { label: "EVV Workflow", path: "/healthcare/evv-workflow", group: "Billing & Finance", keywords: "evv sandata" },
  { label: "Payroll", path: "/healthcare/reports/payroll-summary", group: "Billing & Finance" },
  { label: "Billing Readiness", path: "/healthcare/billing-readiness", group: "Billing & Finance" },
  { label: "Sandata Export", path: "/healthcare/sandata-export", group: "Billing & Finance" },
  { label: "Sandata Aggregator", path: "/healthcare/sandata-aggregator", group: "Billing & Finance", keywords: "sandata evv aggregator pipeline transmit alt-evv wisconsin" },
  { label: "Availity Hub", path: "/healthcare/availity", group: "Billing & Finance", keywords: "availity eligibility forwardhealth" },
  { label: "Eligibility", path: "/healthcare/eligibility", group: "Billing & Finance", hidden: true, keywords: "forwardhealth eligibility check" },
  { label: "Travel & Mileage", path: "/healthcare/travel-log", group: "Billing & Finance", hidden: true, keywords: "mileage" },
  { label: "Hospitalization Safeguards", path: "/healthcare/hosp-safeguards", group: "Billing & Finance", hidden: true, keywords: "hosp hold" },

  // Compliance & Reporting
  { label: "QA & Compliance", path: "/healthcare/compliance-qa", group: "Compliance & Reporting" },
  { label: "PHI Watchdog", path: "/admin/phi-watchdog", group: "Compliance & Reporting", keywords: "phi leak watchdog compliance breach unidentified redacted" },
  { label: "Contact Registry", path: "/admin/contact-registry", group: "Compliance & Reporting", keywords: "contacts phone fax email directory unified phi allowed stub" },
  { label: "User Guide", path: "/healthcare/guide", group: "Compliance & Reporting", keywords: "help manual documentation guide how to instructions" },
  { label: "Audit Trail", path: "/healthcare/audit-log", group: "Compliance & Reporting", keywords: "audit log activity" },
  { label: "Audit Readiness", path: "/healthcare/audit-readiness", group: "Compliance & Reporting", hidden: true },
  { label: "Reports", path: "/healthcare/analytics", group: "Compliance & Reporting", keywords: "analytics" },
  { label: "Predictive Analytics", path: "/healthcare/predictive", group: "Compliance & Reporting", hidden: true, keywords: "ai" },
  { label: "Rules Engine", path: "/healthcare/rules", group: "Compliance & Reporting", hidden: true, keywords: "automation" },

  // Client File & EHR
  { label: "Client File & EHR", path: "/healthcare/documents", group: "Client File & EHR", keywords: "ehr documents records medical" },

  // Supportive Care
  { label: "Supportive Care", path: "/healthcare/supportive-care", group: "Supportive Care" },

  // Communication
  { label: "Fax Center", path: "/healthcare/fax", group: "Communication", keywords: "humblefax" },

  // Admin (hidden sub-pages surfaced via ⌘K)
  { label: "Setup", path: "/healthcare/admin", group: "Admin", keywords: "admin settings" },
  { label: "Form Templates", path: "/healthcare/form-templates", group: "Admin", hidden: true, keywords: "templates pdf upload calibrate" },
  { label: "e-Signatures", path: "/healthcare/e-signatures", group: "Admin", hidden: true },
  { label: "Client Surveys", path: "/healthcare/client-surveys", group: "Admin", hidden: true },
  { label: "Daily Digest", path: "/healthcare/daily-digest", group: "Admin", hidden: true, keywords: "email digest" },
  { label: "Activity Feed", path: "/healthcare/activity-feed", group: "Admin", hidden: true },
  { label: "Dashboard Customizer", path: "/healthcare/dashboard-customizer", group: "Admin", hidden: true, keywords: "layout config" },
  { label: "Batch Operations", path: "/healthcare/batch-operations", group: "Admin", hidden: true, keywords: "bulk" },
  { label: "Live Monitor", path: "/healthcare/live-monitor", group: "Admin", hidden: true, keywords: "visits" },
  { label: "Google Calendar", path: "/healthcare/google-calendar", group: "Admin", hidden: true },
  { label: "Google Drive", path: "/healthcare/google-drive", group: "Admin", hidden: true },
  { label: "Alert Center", path: "/healthcare/alerts", group: "Admin", hidden: true },
  { label: "Notifications", path: "/healthcare/notifications", group: "Admin", hidden: true },

  // Clinical (hidden — prep-work; RN assessment is done in the Clinical App)
  { label: "PCST Assessment", path: "/healthcare/clinical/pcst", group: "Clinical", hidden: true },
  { label: "485 · Cert & POC", path: "/healthcare/clinical/cms485", group: "Clinical", hidden: true },
  { label: "Care Instruction Plans", path: "/healthcare/care-plans", group: "Clinical", hidden: true },
  { label: "Service Care Plans", path: "/healthcare/service-care-plans", group: "Clinical", hidden: true },
  { label: "AI 485 Narratives", path: "/healthcare/ai-narratives", group: "Clinical", hidden: true },
  // iter 508 · "Non-Skilled Assessment" removed from the palette — it is
  // client-scoped and only reachable from a patient record. Adding it
  // back here would violate the "exactly one route" constraint.
];

// Lightweight page filter — prefix > substring > fuzzy, capped to 8 results.
// Keeps rankings predictable (unlike cmdk's fuzzy default).
function filterPages(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const scored = [];
  for (const p of PAGES) {
    const label = p.label.toLowerCase();
    const kw = (p.keywords || "").toLowerCase();
    const group = p.group.toLowerCase();
    let score = 0;
    if (label === q) score = 1000;
    else if (label.startsWith(q)) score = 500;
    else if (label.includes(q)) score = 200;
    else if (kw.includes(q)) score = 120;
    else if (group.includes(q)) score = 40;
    else {
      // Subsequence fuzzy (e.g. "denmgmt" matches "Denial Management")
      let qi = 0;
      for (let i = 0; i < label.length && qi < q.length; i++) {
        if (label[i] === q[qi]) qi++;
      }
      if (qi === q.length) score = 15;
    }
    if (score > 0) scored.push({ ...p, _score: score });
  }
  scored.sort((a, b) => b._score - a._score || a.label.localeCompare(b.label));
  return scored.slice(0, 8);
}

/**
 * GlobalQuickActions — mounts at the app root (inside MainLayout) and
 * provides:
 *   • A globally-callable "+ New Client" modal via window.__avidaQuickActions.openNewLead()
 *   • Keyboard shortcut "N" to open the new-client modal anywhere
 *   • Cmd+K / Ctrl+K command palette — searches clients / pipelines / staff
 *     AND lets users jump to any page (including hidden ones)
 *
 * Uses a window-bus (instead of context hook) for the same reason the
 * UnsavedChangesProvider does: the visual-edits Babel plugin can't trace
 * prop sources through certain context-hook imports in this codebase.
 */
export default function GlobalQuickActions() {
  const [showPalette, setShowPalette] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAvi, setShowAvi] = useState(false);
  // Iter 490 — auto-hide the FAB while any right-side slide-over
  // (Timeline SMS composer, future call-transfer picker, etc.) is
  // mounted. Fixes the "Ask Avi pill sits on top of Send" bug.
  const slideoverOpen = useSlideoverOpen();
  const fabHidden = showPalette || showHelp || showAvi || slideoverOpen;
  const [aviInitialPrompt, setAviInitialPrompt] = useState("");
  const [aviInitialPatientId, setAviInitialPatientId] = useState(null);
  const [aviInitialPatientName, setAviInitialPatientName] = useState("");
  const [aviInitialCommand, setAviInitialCommand] = useState("");
  const navigate = useNavigate();

  // Wire window bus so any component (sidebar buttons, dashboard chips) can
  // trigger the palettes without prop drilling.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // window.__avidaAvi.open() → just opens the drawer.
    // window.__avidaAvi.open({ prompt: "…" }) → opens AND seeds the composer.
    // window.__avidaAvi.open("…") → shorthand (positional string == prompt).
    const openAvi = (arg) => {
      let prompt = "";
      let patientId = null;
      let patientName = "";
      let command = "";
      if (typeof arg === "string") prompt = arg;
      else if (arg && typeof arg === "object") {
        if (typeof arg.prompt === "string") prompt = arg.prompt;
        if (arg.patientId) patientId = arg.patientId;
        if (arg.patientName) patientName = arg.patientName;
        if (typeof arg.command === "string") command = arg.command;
      }
      setAviInitialPrompt(prompt);
      setAviInitialPatientId(patientId);
      setAviInitialPatientName(patientName);
      setAviInitialCommand(command);
      setShowPalette(false);
      setShowHelp(false);
      setShowAvi(true);
    };
    window.__avidaQuickActions = {
      openSearch: () => { setShowHelp(false); setShowAvi(false); setShowPalette(true); },
      openHelp:   () => { setShowPalette(false); setShowAvi(false); setShowHelp(true); },
      openAvi:    openAvi,
    };
    window.__avidaAvi = { open: openAvi, close: () => setShowAvi(false) };
    return () => {
      delete window.__avidaQuickActions;
      delete window.__avidaAvi;
    };
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      // Cmd/Ctrl+K → search palette
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setShowHelp(false);
        setShowPalette(true);
        return;
      }
      // "?" → help palette (but not while typing in an input/textarea)
      const t = e.target;
      const isTypingContext =
        t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (!isTypingContext && !e.metaKey && !e.ctrlKey && !e.altKey && e.key === "?") {
        e.preventDefault();
        setShowPalette(false);
        setShowHelp(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      {showPalette && (
        <SearchPalette onClose={() => setShowPalette(false)} navigate={navigate} />
      )}
      {showHelp && (
        <HelpPalette onClose={() => setShowHelp(false)} />
      )}
      <AviChatDrawer
        open={showAvi}
        onClose={() => { setShowAvi(false); setAviInitialPrompt(""); setAviInitialPatientId(null); setAviInitialPatientName(""); setAviInitialCommand(""); }}
        initialPrompt={aviInitialPrompt}
        initialPatientId={aviInitialPatientId}
        initialPatientName={aviInitialPatientName}
        initialCommand={aviInitialCommand}
      />
      {/* Ask Avi launch FAB — the ONLY floating bottom-right FAB.
          Per the /phone rebuild spec (Part 0.6), the yellow "?" Help
          FAB was removed because it overlapped Ask Avi. Users still
          reach shortcuts via Cmd+K (handled by the global keydown
          handler above) and the User Manual via the sidebar profile
          menu / window.__avidaOpenManual bus. */}
      <button
        type="button"
        onClick={() => { setShowPalette(false); setShowHelp(false); setShowAvi(true); }}
        className={`fixed bottom-24 sm:bottom-5 right-4 sm:right-5 z-[55] px-3.5 h-11 rounded-full flex items-center gap-2 shadow-lg transition-all hover:scale-[1.03] hover:shadow-xl focus-visible:ring-2 focus-visible:ring-[#FFDA00] focus-visible:outline-none ${fabHidden ? "opacity-0 pointer-events-none scale-95" : "opacity-100"}`}
        style={{ background: "#032866", color: "#FFDA00", border: "2px solid #FFDA00" }}
        aria-label="Ask Avi — AI compliance assistant"
        aria-hidden={fabHidden}
        title="Ask Avi (AI compliance assistant)"
        data-testid="ask-avi-fab"
      >
        <Sparkles className="w-4 h-4" strokeWidth={2.5} />
        <span className="text-xs font-bold tracking-wide">Ask Avi</span>
      </button>
    </>
  );
}

/* ───────────────────────────────────────────────────────────────
   Cmd+K command palette
   ─────────────────────────────────────────────────────────────── */
function SearchPalette({ onClose, navigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const debRef = useRef(null);

  // Auto-focus input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 30);
    // Load recent clients for the empty-query state
    fetch(`${API}/api/search/recent-clients?limit=5`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    }).then(r => r.json()).then(d => setRecent(d.items || [])).catch(() => {});
  }, []);

  // Debounced search
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/search/global?q=${encodeURIComponent(q)}&limit=20`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const d = await r.json();
        setResults(d.results || []);
        setActiveIdx(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, [q]);

  const pageHits = useMemo(() => filterPages(q), [q]);

  const list = q.trim() ? results : recent.map(r => ({
    type: "pipeline",
    id: r.id,
    title: r.patient_name,
    subtitle: `Recently edited · ${r.updated_at ? formatDate(r.updated_at) : ""}`,
    path: r.path,
  }));

  const goTo = (item) => {
    if (item?.path) navigate(item.path);
    onClose?.();
  };

  // Keyboard navigation within the palette — spans records + pages.
  // Layout: records first (list), then pages (pageHits).
  const combinedLen = list.length + pageHits.length;
  const onKeyDown = (e) => {
    if (e.key === "Escape") return onClose?.();
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(combinedLen - 1, i + 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    if (e.key === "Enter")     {
      e.preventDefault();
      const all = [...list, ...pageHits];
      if (all[activeIdx]) goTo(all[activeIdx]);
    }
  };

  const iconForType = (t) => {
    if (t === "pipeline") return Sparkles;
    if (t === "client")   return UserIcon;
    if (t === "lead")     return Phone;
    if (t === "user")     return UserIcon;
    return FileText;
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center pt-[10vh] bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="search-palette"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search clients, staff, pages…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder-slate-400"
            data-testid="search-palette-input"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">esc</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="search-palette-results">
          {!q.trim() && recent.length > 0 && (
            <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Recently edited
            </div>
          )}
          {q.trim() && list.length === 0 && pageHits.length === 0 && !loading && (
            <div className="py-12 text-center" data-testid="search-palette-empty">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No matches for &ldquo;{q}&rdquo;</p>
            </div>
          )}
          {/* Records (clients/leads/pipelines/staff) */}
          {list.length > 0 && (
            <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {q.trim() ? "Records" : "Recently edited"}
            </div>
          )}
          {list.map((item, i) => {
            const Icon = iconForType(item.type);
            const active = i === activeIdx;
            return (
              <button
                key={`${item.type}-${item.id}`}
                onClick={() => goTo(item)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${active ? "bg-amber-50" : "hover:bg-slate-50"}`}
                data-testid={`search-result-${item.type}-${item.id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-slate-900 truncate">{item.title}</p>
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                      {item.type}
                    </span>
                  </div>
                  <p className="text-[12px] text-slate-500 truncate">{item.subtitle}</p>
                </div>
                <ArrowRight className={`w-4 h-4 shrink-0 ${active ? "text-amber-600" : "text-slate-300"}`} />
              </button>
            );
          })}

          {/* Pages (navigation) */}
          {pageHits.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-t border-slate-100 mt-1">
                Jump to page
              </div>
              {pageHits.map((p, i) => {
                const idx = list.length + i;
                const active = idx === activeIdx;
                return (
                  <button
                    key={`page-${p.path}-${p.label}`}
                    onClick={() => goTo(p)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${active ? "bg-amber-50" : "hover:bg-slate-50"}`}
                    data-testid={`page-result-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <Compass className="w-4 h-4 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900 truncate">{p.label}</p>
                        {p.hidden ? (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">
                            hidden
                          </span>
                        ) : null}
                      </div>
                      <p className="text-[11px] text-slate-400 truncate">{p.group}</p>
                    </div>
                    <ArrowRight className={`w-4 h-4 shrink-0 ${active ? "text-amber-600" : "text-slate-300"}`} />
                  </button>
                );
              })}
            </>
          )}
        </div>

        <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400 bg-slate-50">
          <div className="flex items-center gap-3">
            <span><kbd className="font-mono px-1 bg-white border border-slate-200 rounded">↑↓</kbd> navigate</span>
            <span><kbd className="font-mono px-1 bg-white border border-slate-200 rounded">↵</kbd> open</span>
          </div>
        </div>
      </div>
    </div>
  );
}
